import { AxiosRequestConfig } from 'axios'
import {
  LkPublication as Data,
  LkPublicationCollection as Collection,
  LkPublicationStorage as Storage,
  LkPublicationStorageFileWithMetaCollection as StorageFilesCollection,
} from '@/typings/api/Publication'
import {
  BaseApiParams,
  BaseApiResponse,
  BaseApiResponseCollection,
} from '@/typings/common'

import { BaseLkApiAuth } from '@/api/BaseApiAuth'


type BR = BaseApiResponse<Data>

type BRC = BaseApiResponseCollection<never> & {
  publications: Collection[];
}

const updateMetaInResponse = (response: { count: number; meta: unknown }) => {
  if (response && 'count' in response && !response.meta) {
    // eslint-disable-next-line no-param-reassign
    response.meta = { count: response.count }
  }
  return response
}

export class LkPublicationsApi extends BaseLkApiAuth<
  Data,
  Collection,
  BaseApiParams,
  BR,
  BRC
  > {
  protected readonly endpoint = 'publications';

  public setInterceptors() {
    super.setInterceptors()

    // @ts-ignore TODO:
    this.http.interceptors.response.use(updateMetaInResponse)
  }

  public getOneBySlug<P extends BaseApiParams>(
    slug: Data['slug'], rawParams?: P, config?: AxiosRequestConfig,
  ) {
    const path = `${slug}/`
    const [url, params] = this.getUrl(path, rawParams)
    return this.request<never, Data>('GET', url, { params, ...config })
  }

  public getStorages<P extends BaseApiParams>(
    publicationId: Data['id'], rawParams?: P, config?: AxiosRequestConfig,
  ) {
    const path = `${publicationId}/storages`
    const [url, params] = this.getUrl(path, rawParams)
    return this.request<null, { data: NonNullable<Data['storages']> }>('GET', url, { params, ...config })
  }

  public getStorageFiles<P extends BaseApiParams>(
    publicationId: Data['id'], storageId: Storage['id'], rawParams?: P, config?: AxiosRequestConfig,
  ) {
    const path = `${publicationId}/storages/${storageId}/files`
    const [url, params] = this.getUrl(path, rawParams)
    return this.request<null, { data: NonNullable<StorageFilesCollection> }>('GET', url, { params, ...config })
  }

  public getStorageFilesCount<P extends BaseApiParams>(
    publicationId: Data['id'], storageId: Storage['id'], rawParams?: P, config?: AxiosRequestConfig,
  ) {
    const path = `${publicationId}/storages/${storageId}/files/count`
    const [url, params] = this.getUrl(path, rawParams)
    return this.request<null, { count: number }>('GET', url, { params, ...config })
  }

  public downloadStorageFile<P extends BaseApiParams>(publicationId: Data['id'], storageId: Storage['id'], storageFileId: number, rawParams?: P, config?: AxiosRequestConfig) {
    const path = `${publicationId}/storages/${storageId}/files/${storageFileId}/download`
    const [url, params] = this.getUrl(path, rawParams)
    return this.request('GET', url, { params, ...config })
  }

  public getDataCollections<P extends BaseApiParams>(
    publicationId: Data['id'], rawParams?: P, config?: AxiosRequestConfig,
  ) {
    const path = `${publicationId}/data_collections`
    const [url, params] = this.getUrl(path, rawParams)
    return this.request<null, { data: NonNullable<Data['data_collections']> }>('GET', url, { params, ...config })
  }

  public getDefault(data?: Record<string, unknown>, config = {}) {
    const path = 'redirect'
    const [url, params] = this.getUrl(path, data)
    return this.request<null, Data>('GET', url, { params, ...config })
  }

  public read(id: Data['id'], params: Record<string, unknown> = {}, config: AxiosRequestConfig = {}) {
    const path = `${id}/viewed`
    const [url] = this.getUrl(path, params)
    return this.request<never, never>('POST', url, { params, ...config })
  }

  public addStarred(id: Data['id'], config: AxiosRequestConfig = {}) {
    const path = `${id}/starred/add`
    const [url] = this.getUrl(path)
    return this.request<never, void>('POST', url, config)
  }

  public deleteStarred(id: Data['id'], config: AxiosRequestConfig = {}) {
    const path = `${id}/starred/delete`
    const [url] = this.getUrl(path)
    return this.request<never, void>('POST', url, config)
  }
}
